import { fbt } from 'fbtee'
import { Button } from '@/components/ui/button'
import { formatErrorMessage, type ClassifiedError } from '@/lib/errors'
import { useAuth } from '@/hooks/useAuth'
import { openFeedback } from '@/lib/feedbackBridge'
import { retryButtonLabel, goHomeButtonLabel } from '@/lib/commonStrings'
import { useTransitionNavigate } from '@/lib/viewTransition'

function titleForKind(kind: ClassifiedError['kind']): string {
  const titles: Record<ClassifiedError['kind'], string> = {
    network: fbt('Connection problem', 'Error page title for network connectivity failures'),
    permission: fbt('Access denied', 'Error page title for permission or auth failures'),
    'not-found': fbt('Not found', 'Error page title when a resource cannot be found'),
    unknown: fbt('Something went wrong', 'Error page title for unexpected errors'),
  }
  return titles[kind]
}

export function ErrorState({
  error,
  onRetry,
}: {
  error: ClassifiedError
  onRetry?: () => void
}) {
  const navigate = useTransitionNavigate()
  const { user } = useAuth()

  let action: { label: string; onClick: () => void } | null = null
  if (error.kind === 'network' || error.kind === 'unknown') {
    action = {
      label: retryButtonLabel(),
      onClick: onRetry ?? (() => window.location.reload()),
    }
  } else if (error.kind === 'not-found') {
    action = {
      label: goHomeButtonLabel(),
      onClick: () => navigate('/', { replace: true }),
    }
  } else if (error.kind === 'permission' && !user) {
    action = {
      label: fbt('Sign in', 'Button to navigate to the sign-in page after a permission error'),
      onClick: () => navigate('/login'),
    }
  }

  return (
    <div className="min-h-dvh bg-background flex items-center justify-center p-6">
      <div className="max-w-md text-center space-y-3">
        <h2 className="text-lg font-semibold">{titleForKind(error.kind)}</h2>
        <p className="text-muted-foreground text-sm">{formatErrorMessage(error)}</p>
        <div className="pt-2 flex gap-2 justify-center">
          {action && <Button onClick={action.onClick}>{action.label}</Button>}
          {user && (
            <Button variant="outline" onClick={() => openFeedback({ error })}>
              <fbt desc="Button to report an error to the developer">Report this</fbt>
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
